import { useEffect, useRef, useState } from "react";
import { RecaptchaBlock } from "../components/RecaptchaBlock";
import { toast } from "sonner";
import { Shield, Globe, Users, Zap, HeartPulse, FlaskConical, ToggleRight, Save, Trash2, PlusCircle, AlertTriangle, Home, Plug, CreditCard, ScrollText, Tags, UserCog, Search, Download, X, Ban, RotateCcw, UserPlus, KeyRound, Copy, ShieldCheck } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";

const TABS = [
  { id: "users", label: "Usuarios", icon: UserCog },
  { id: "flags", label: "Feature Flags", icon: ToggleRight },
  { id: "trading", label: "Trading Real", icon: Zap },
  { id: "countries", label: "Países", icon: Globe },
  { id: "plans", label: "Planes", icon: Users },
  { id: "families", label: "Familias", icon: Home },
  { id: "integrations", label: "Integraciones", icon: Plug },
  { id: "subscriptions", label: "Suscripciones", icon: CreditCard },
  { id: "categories", label: "Categorías globales", icon: Tags },
  { id: "audit", label: "Auditoría", icon: ScrollText },
  { id: "health", label: "Salud", icon: HeartPulse },
  { id: "experiments", label: "Experimentos", icon: FlaskConical },
];

export default function Matriz() {
  const { isMatriz, loading } = useFeatureFlags();
  const [tab, setTab] = useState("users");

  if (loading) {
    return <div className="text-sm text-muted-foreground">Cargando…</div>;
  }
  if (!isMatriz) {
    return (
      <div className="max-w-lg mx-auto mt-12 rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-center" data-testid="matriz-forbidden">
        <AlertTriangle className="mx-auto mb-3" size={28} />
        <h1 className="font-heading text-xl font-bold">Acceso restringido</h1>
        <p className="text-sm text-muted-foreground mt-1">Este panel es exclusivo de la familia MATRIZ (Super Admin).</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="matriz-panel">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
          <Shield size={20} strokeWidth={2.5} />
        </div>
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Panel MATRIZ</h1>
          <p className="text-xs text-muted-foreground">Administración global de RadarUpFinance · exclusivo Super Admin</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`matriz-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm font-medium transition-colors ${
                tab === t.id
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "users" && <UsersTab />}
      {tab === "flags" && <FlagsTab />}
      {tab === "trading" && <TradingLimitsTab />}
      {tab === "countries" && <CountriesTab />}
      {tab === "plans" && <PlansTab />}
      {tab === "families" && <FamiliesTab />}
      {tab === "integrations" && <IntegrationsTab />}
      {tab === "subscriptions" && <SubscriptionsTab />}
      {tab === "categories" && <CategoriesGlobalTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "health" && <HealthTab />}
      {tab === "experiments" && <ExperimentsTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FEATURE FLAGS
// ---------------------------------------------------------------------------
function FlagsTab() {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addForm, setAddForm] = useState({ scope: "global", flag: "trading_real_users", key: "", enabled: true });

  const load = async () => {
    try {
      const [f, u, c, p] = await Promise.all([
        api.get("/matriz/flags"),
        api.get("/matriz/users?q=").catch(() => ({ data: { users: [] } })),
        api.get("/matriz/countries"),
        api.get("/matriz/plans"),
      ]);
      setData(f.data);
      setUsers(u.data.users || []);
      setCountries(c.data);
      setPlans(p.data.plans || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Cargando…</div>;

  const submitOverride = async () => {
    setBusy(true);
    try {
      const { scope, flag, key, enabled } = addForm;
      if (scope === "global") {
        await api.put("/matriz/flags/global", { flag, enabled });
      } else if (scope === "country") {
        await api.put("/matriz/flags/country", { flag, key, enabled });
      } else if (scope === "plan") {
        await api.put("/matriz/flags/plan", { flag, key, enabled });
      } else if (scope === "user") {
        await api.put("/matriz/flags/user", { flag, key, enabled });
      }
      toast.success("Override guardado");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const removeOverride = async (scope, key, flag) => {
    if (!confirm(`¿Eliminar override ${scope}/${key || "*"}/${flag}?`)) return;
    try {
      await api.delete("/matriz/flags/override", { data: { scope, key: key || "*", flag } });
      toast.success("Override eliminado");
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const knownFlagKeys = Object.keys(data.known_flags);
  const allScopes = ["global", "country", "plan", "user", "family"];

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-border p-4 bg-secondary/20">
        <div className="text-sm font-semibold mb-3 flex items-center gap-2"><PlusCircle size={16} /> Añadir / actualizar override</div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
          <Select value={addForm.scope} onValueChange={(v) => setAddForm({ ...addForm, scope: v, key: v === "global" ? "*" : "" })}>
            <SelectTrigger data-testid="flag-scope-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="global">Global</SelectItem>
              <SelectItem value="country">País</SelectItem>
              <SelectItem value="plan">Plan</SelectItem>
              <SelectItem value="user">Usuario</SelectItem>
            </SelectContent>
          </Select>
          <Select value={addForm.flag} onValueChange={(v) => setAddForm({ ...addForm, flag: v })}>
            <SelectTrigger data-testid="flag-flag-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {knownFlagKeys.map((k) => <SelectItem key={k} value={k}>{data.known_flags[k].label}</SelectItem>)}
            </SelectContent>
          </Select>
          {addForm.scope === "country" && (
            <Select value={addForm.key} onValueChange={(v) => setAddForm({ ...addForm, key: v })}>
              <SelectTrigger><SelectValue placeholder="País" /></SelectTrigger>
              <SelectContent>
                {(countries?.catalog || []).map((c) => <SelectItem key={c.code} value={c.code}>{c.name} ({c.code})</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {addForm.scope === "plan" && (
            <Select value={addForm.key} onValueChange={(v) => setAddForm({ ...addForm, key: v })}>
              <SelectTrigger><SelectValue placeholder="Plan" /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {addForm.scope === "user" && (
            <Select value={addForm.key} onValueChange={(v) => setAddForm({ ...addForm, key: v })}>
              <SelectTrigger><SelectValue placeholder="Usuario" /></SelectTrigger>
              <SelectContent>
                {users.slice(0, 100).map((u) => <SelectItem key={u.id} value={u.id}>{u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {addForm.scope === "global" && <div className="text-xs text-muted-foreground flex items-center px-2">Aplica a todo el sistema</div>}
          <div className="flex items-center gap-2 px-2">
            <Switch checked={addForm.enabled} onCheckedChange={(v) => setAddForm({ ...addForm, enabled: v })} data-testid="flag-enabled-switch" />
            <span className="text-xs">{addForm.enabled ? "Activado" : "Desactivado"}</span>
          </div>
          <Button onClick={submitOverride} disabled={busy || (addForm.scope !== "global" && !addForm.key)} data-testid="flag-save-btn">
            <Save size={14} className="mr-1" /> Guardar
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {allScopes.map((scope) => {
          const rows = data.overrides[scope] || [];
          if (!rows.length && scope !== "global") return null;
          return (
            <div key={scope} className="rounded-xl border border-border overflow-hidden">
              <div className="px-4 py-2 bg-secondary/40 text-xs font-semibold uppercase tracking-wider">Scope: {scope} ({rows.length})</div>
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="text-left px-4 py-2">Key</th>
                    <th className="text-left px-4 py-2">Flag</th>
                    <th className="text-left px-4 py-2">Estado</th>
                    <th className="text-left px-4 py-2">Actualizado</th>
                    <th className="text-right px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-3 text-xs text-muted-foreground italic">Sin overrides</td></tr>}
                  {rows.map((r, i) => (
                    <tr key={`${scope}-${r.key}-${r.flag}-${i}`} className="border-t border-border" data-testid={`flag-row-${scope}-${r.flag}`}>
                      <td className="px-4 py-2 font-mono text-xs">{r.key || "*"}</td>
                      <td className="px-4 py-2">{r.flag}</td>
                      <td className="px-4 py-2">
                        {r.enabled ? <Badge className="bg-emerald-500 text-white">ON</Badge> : <Badge variant="secondary">OFF</Badge>}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{r.updated_at ? new Date(r.updated_at).toLocaleString("es-ES") : "—"}</td>
                      <td className="px-4 py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => removeOverride(scope, r.key, r.flag)} data-testid={`flag-delete-${scope}-${r.flag}`}>
                          <Trash2 size={14} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TRADING LIMITS
// ---------------------------------------------------------------------------
function TradingLimitsTab() {
  const [limits, setLimits] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/matriz/trading-limits"); setLimits(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/matriz/trading-limits", {
        max_amount_per_order_eur: Number(limits.max_amount_per_order_eur),
        max_orders_per_day: Number(limits.max_orders_per_day),
        max_total_exposure_eur: Number(limits.max_total_exposure_eur),
        allowed_order_types: limits.allowed_order_types,
        dry_run: limits.dry_run,
      });
      setLimits(data);
      toast.success("Límites actualizados");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const toggleType = (t) => {
    const set = new Set(limits.allowed_order_types || []);
    if (set.has(t)) set.delete(t); else set.add(t);
    setLimits({ ...limits, allowed_order_types: Array.from(set) });
  };

  if (!limits) return <div className="text-sm text-muted-foreground">Cargando…</div>;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
        <div className="flex items-center gap-2 font-semibold text-amber-900 dark:text-amber-200"><AlertTriangle size={16} /> Nunca se ejecutan órdenes sin límites</div>
        <p className="text-xs text-amber-900/80 dark:text-amber-200/80 mt-1">
          El robot Real siempre respeta estos topes. En cualquier momento la MATRIZ puede activar/desactivar el <b>dry-run</b> (simulación sin envío al exchange).
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Máximo por orden (€)" v={limits.max_amount_per_order_eur} onChange={(v) => setLimits({ ...limits, max_amount_per_order_eur: v })} testid="limit-max-order" />
        <Field label="Máximo órdenes / día" v={limits.max_orders_per_day} onChange={(v) => setLimits({ ...limits, max_orders_per_day: v })} testid="limit-max-day" />
        <Field label="Exposición total máxima (€)" v={limits.max_total_exposure_eur} onChange={(v) => setLimits({ ...limits, max_total_exposure_eur: v })} testid="limit-max-exposure" />
      </div>

      <Card className="p-4">
        <div className="text-sm font-semibold mb-3">Tipos de orden permitidos</div>
        <div className="flex gap-3">
          {["MARKET", "LIMIT"].map((t) => (
            <label key={t} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`order-type-${t.toLowerCase()}`}>
              <input type="checkbox" checked={(limits.allowed_order_types || []).includes(t)} onChange={() => toggleType(t)} />
              {t}
            </label>
          ))}
        </div>
        <div className="text-xs text-muted-foreground mt-2">STOP LOSS, TRAILING STOP y OCO se activarán en fases posteriores.</div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Modo Dry-Run</div>
            <div className="text-xs text-muted-foreground">Cuando está activo, las órdenes se registran auditadas pero no se envían al exchange.</div>
          </div>
          <Switch checked={!!limits.dry_run} onCheckedChange={(v) => setLimits({ ...limits, dry_run: v })} data-testid="limit-dryrun-switch" />
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="limits-save-btn">
          <Save size={14} className="mr-1" /> Guardar límites
        </Button>
      </div>
    </div>
  );
}

function Field({ label, v, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground">{label}</span>
      <Input type="number" step="0.01" value={v} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </label>
  );
}

// ---------------------------------------------------------------------------
// COUNTRIES
// ---------------------------------------------------------------------------
function CountriesTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await api.get("/matriz/countries"); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Cargando…</div>;

  const toggle = (list, code) => {
    const set = new Set(data[list] || []);
    if (set.has(code)) set.delete(code); else set.add(code);
    setData({ ...data, [list]: Array.from(set) });
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/matriz/countries", { allowed: data.allowed, blocked: data.blocked });
      setData(r.data);
      toast.success("Países actualizados");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-muted-foreground">
        Marca los países en los que Trading Real está <b>permitido</b> o <b>bloqueado</b>. Se aplica sobre el país declarado en la unidad familiar.
        La arquitectura queda preparada para futuras reglas regulatorias detalladas.
      </div>
      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-4 py-2">País</th>
              <th className="text-left px-4 py-2">Código</th>
              <th className="text-center px-4 py-2">Permitido</th>
              <th className="text-center px-4 py-2">Bloqueado</th>
            </tr>
          </thead>
          <tbody>
            {data.catalog.map((c) => (
              <tr key={c.code} className="border-t border-border" data-testid={`country-row-${c.code}`}>
                <td className="px-4 py-2">{c.name}</td>
                <td className="px-4 py-2 font-mono text-xs">{c.code}</td>
                <td className="text-center px-4 py-2">
                  <input type="checkbox" checked={(data.allowed || []).includes(c.code)} onChange={() => toggle("allowed", c.code)} data-testid={`country-allowed-${c.code}`} />
                </td>
                <td className="text-center px-4 py-2">
                  <input type="checkbox" checked={(data.blocked || []).includes(c.code)} onChange={() => toggle("blocked", c.code)} data-testid={`country-blocked-${c.code}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="countries-save-btn">
          <Save size={14} className="mr-1" /> Guardar
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PLANS
// ---------------------------------------------------------------------------
function PlansTab() {
  const [plans, setPlans] = useState([]);
  const load = async () => {
    try { const r = await api.get("/matriz/plans"); setPlans(r.data.plans || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    try {
      const r = await api.put("/matriz/plans", { code: p.code, enabled: !p.enabled });
      setPlans(r.data.plans || []);
      toast.success(`Plan ${p.label} ${!p.enabled ? "activado" : "desactivado"}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="text-xs text-muted-foreground">
        Arquitectura de planes preparada para RadarUpFinance. Activa los planes disponibles para contratación.
        Los planes marcados como <b>futuro</b> mantienen su estructura reservada para evoluciones (limitaciones se definirán más adelante).
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {plans.map((p) => (
          <Card key={p.code} className="p-4 flex items-start justify-between" data-testid={`plan-row-${p.code}`}>
            <div>
              <div className="flex items-center gap-2">
                <div className="font-heading font-bold">{p.label}</div>
                {p.future && <Badge variant="secondary" className="text-xs">Futuro</Badge>}
              </div>
              <div className="text-xs text-muted-foreground mt-1">{p.description}</div>
              <div className="text-xs font-mono text-muted-foreground mt-1">code: {p.code}</div>
            </div>
            <Switch checked={p.enabled} onCheckedChange={() => toggle(p)} data-testid={`plan-switch-${p.code}`} />
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HEALTH
// ---------------------------------------------------------------------------
function HealthTab() {
  const [data, setData] = useState(null);
  const load = async () => {
    try { const r = await api.get("/matriz/health"); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); const id = setInterval(load, 30_000); return () => clearInterval(id); }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Cargando…</div>;

  const cards = [
    { k: "users_total", label: "Usuarios totales" },
    { k: "families_total", label: "Familias" },
    { k: "robots_active", label: "Robots activos" },
    { k: "emergency_stops", label: "Emergency stops" },
    { k: "orders_real_24h", label: "Órdenes reales 24h" },
    { k: "paper_trades_24h", label: "Paper trades 24h" },
    { k: "positions_open", label: "Posiciones abiertas" },
    { k: "snaptrade_users", label: "SnapTrade users" },
    { k: "notifications_24h", label: "Notificaciones 24h" },
  ];
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-3">Actualización cada 30s · {new Date(data.at).toLocaleString("es-ES")}</div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <Card key={c.k} className="p-4" data-testid={`health-card-${c.k}`}>
            <div className="text-xs text-muted-foreground">{c.label}</div>
            <div className="font-heading text-2xl font-extrabold">{data[c.k] ?? "—"}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EXPERIMENTS
// ---------------------------------------------------------------------------
function ExperimentsTab() {
  const [data, setData] = useState(null);
  const load = async () => {
    try { const r = await api.get("/matriz/robot/experiments?limit=100"); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-sm text-muted-foreground">Cargando…</div>;

  return (
    <div className="space-y-6">
      <Section title={`Órdenes REALES (${data.real?.length || 0})`} testid="experiments-real">
        {(!data.real || data.real.length === 0) ? (
          <div className="text-xs text-muted-foreground italic">Sin órdenes reales registradas todavía.</div>
        ) : (
          <SimpleTable rows={data.real} cols={[
            { k: "created_at", label: "Fecha", f: (v) => new Date(v).toLocaleString("es-ES") },
            { k: "exchange", label: "Exchange" }, { k: "symbol", label: "Símbolo" }, { k: "side", label: "Lado" },
            { k: "order_type", label: "Tipo" }, { k: "quote_qty_eur", label: "€", f: (v) => Number(v).toFixed(2) },
            { k: "dry_run", label: "Dry-run", f: (v) => v ? "Sí" : "No" },
          ]} />
        )}
      </Section>

      <Section title={`Paper trades (${data.paper?.length || 0})`} testid="experiments-paper">
        <SimpleTable rows={data.paper || []} cols={[
          { k: "time_ms", label: "Fecha", f: (v) => new Date(v).toLocaleString("es-ES") },
          { k: "symbol", label: "Símbolo" }, { k: "side", label: "Lado" },
          { k: "price", label: "Precio", f: (v) => Number(v).toFixed(4) },
          { k: "qty", label: "Cantidad", f: (v) => Number(v).toFixed(6) },
          { k: "pnl_eur", label: "P&L €", f: (v) => v == null ? "—" : Number(v).toFixed(2) },
          { k: "reason", label: "Motivo" },
        ]} />
      </Section>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="text-sm font-semibold mb-2">{title}</div>
      <div className="rounded-xl border border-border overflow-x-auto">{children}</div>
    </div>
  );
}

function SimpleTable({ rows, cols }) {
  if (!rows || rows.length === 0) return <div className="p-4 text-xs text-muted-foreground italic">Sin datos.</div>;
  return (
    <table className="w-full text-sm">
      <thead className="bg-muted/40">
        <tr>{cols.map((c) => <th key={c.k} className="text-left px-3 py-2 text-xs font-semibold">{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {rows.slice(0, 200).map((row, i) => (
          <tr key={i} className="border-t border-border">
            {cols.map((c) => <td key={c.k} className="px-3 py-2 text-xs">{c.f ? c.f(row[c.k]) : (row[c.k] ?? "—")}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}


// ---------------------------------------------------------------------------
// FAMILIES
// ---------------------------------------------------------------------------
function FamiliesTab() {
  const [families, setFamilies] = useState([]);
  const [q, setQ] = useState("");
  const [plans, setPlans] = useState([]);
  const [countries, setCountries] = useState([]);

  const load = async () => {
    try {
      const [f, p, c] = await Promise.all([
        api.get(`/matriz/families${q ? `?q=${encodeURIComponent(q)}` : ""}`),
        api.get("/matriz/plans"),
        api.get("/matriz/countries"),
      ]);
      setFamilies(f.data.families || []);
      setPlans(p.data.plans || []);
      setCountries(c.data.catalog || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const updateFamily = async (id, patch) => {
    try {
      await api.put(`/matriz/families/${id}`, patch);
      toast.success("Familia actualizada");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="families-tab">
      <div className="flex gap-2">
        <Input placeholder="Buscar por nombre/id/país" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="families-search" />
        <Button onClick={load} data-testid="families-search-btn">Buscar</Button>
      </div>
      <div className="rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40"><tr>
            <th className="text-left px-3 py-2">Familia</th>
            <th className="text-left px-3 py-2">Plan</th>
            <th className="text-left px-3 py-2">País</th>
            <th className="text-left px-3 py-2">Miembros</th>
            <th className="text-left px-3 py-2">ID</th>
          </tr></thead>
          <tbody>
            {families.length === 0 && <tr><td colSpan={5} className="p-4 text-xs text-muted-foreground italic">Sin familias</td></tr>}
            {families.map((f) => (
              <tr key={f.id} className="border-t border-border" data-testid={`family-row-${f.id}`}>
                <td className="px-3 py-2 text-sm font-medium">{f.name || "—"}</td>
                <td className="px-3 py-2">
                  <Select value={f.plan || "individual"} onValueChange={(v) => updateFamily(f.id, { plan: v })}>
                    <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {plans.filter((p) => p.enabled).map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2">
                  <Select value={f.country || ""} onValueChange={(v) => updateFamily(f.id, { country: v })}>
                    <SelectTrigger className="h-8 w-28"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-2 text-xs">{f.members}</td>
                <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{f.id.slice(0, 8)}…</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-muted-foreground">Total: {families.length}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// INTEGRATIONS
// ---------------------------------------------------------------------------
function IntegrationsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/matriz/integrations").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Cargando…</div>;
  return (
    <div className="space-y-6" data-testid="integrations-tab">
      <RecaptchaSettingsCard />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {Object.entries(data.integrations).map(([name, info]) => (
        <Card key={name} className="p-4" data-testid={`integration-${name}`}>
          <div className="flex items-center justify-between">
            <div className="font-heading font-bold capitalize">{name.replace("_", " ")}</div>
            <div className="flex items-center gap-1">
              {info.configured ? <Badge className="bg-emerald-500 text-white">Credenciales OK</Badge> : <Badge variant="secondary">Sin credenciales</Badge>}
              {info.flag && (info.global_enabled ? <Badge className="bg-primary text-primary-foreground">Flag ON</Badge> : <Badge variant="outline">Flag OFF</Badge>)}
            </div>
          </div>
          <div className="text-xs text-muted-foreground mt-1">Categoría: {info.category}{info.flag ? ` · Feature flag: ${info.flag}` : " · No requiere flag"}</div>
        </Card>
      ))}
      </div>
    </div>
  );
}

const MODE_LABELS = { v2: "Solo v2 (checkbox «No soy un robot»)", v3: "Solo v3 (invisible, puntuación)", both: "Ambos (v2 + v3)" };

function RecaptchaSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ enabled: false, mode: "v2", v2_site_key: "", v2_secret_key: "", v3_site_key: "", v3_secret_key: "", v3_min_score: 0.5 });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/matriz/recaptcha").then((r) => {
    setCfg(r.data);
    setForm((f) => ({ ...f, enabled: r.data.enabled, mode: r.data.mode, v2_site_key: r.data.v2_site_key, v3_site_key: r.data.v3_site_key, v3_min_score: r.data.v3_min_score, v2_secret_key: "", v3_secret_key: "" }));
  }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/matriz/recaptcha", { ...form, v3_min_score: Number(form.v3_min_score) });
      setCfg(data);
      setForm((f) => ({ ...f, v2_secret_key: "", v3_secret_key: "" }));
      toast.success(data.active ? "reCAPTCHA activo en el login" : data.enabled ? "Guardado. Verifica las claves para activarlo en el login" : "Configuración guardada (reCAPTCHA desactivado)");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  if (!cfg) return <div className="text-sm text-muted-foreground">Cargando reCAPTCHA…</div>;
  const showV2 = form.mode !== "v3";
  const showV3 = form.mode !== "v2";
  return (
    <Card className="p-5 space-y-4" data-testid="recaptcha-settings-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-heading font-bold flex items-center gap-2"><ShieldCheck size={16} /> Google reCAPTCHA</div>
          <p className="text-xs text-muted-foreground mt-1">Protección anti-bots del login. El widget solo aparece cuando está activado con claves validadas por Google. Los secretos se guardan cifrados.</p>
        </div>
        {cfg.active ? <Badge className="bg-emerald-500 text-white" data-testid="recaptcha-status-badge">Activo en login</Badge> : cfg.enabled ? <Badge className="bg-amber-500 text-white" data-testid="recaptcha-status-badge">Pendiente de verificar claves</Badge> : <Badge variant="secondary" data-testid="recaptcha-status-badge">Inactivo</Badge>}
      </div>
      <div className="flex flex-wrap items-center gap-6">
        <label className="flex items-center gap-2 text-sm"><Switch checked={form.enabled} onCheckedChange={set("enabled")} data-testid="recaptcha-enabled-switch" /> Activar plugin</label>
        <div className="flex items-center gap-2 text-sm">
          <span>Modo</span>
          <Select value={form.mode} onValueChange={set("mode")}>
            <SelectTrigger className="w-72 h-9" data-testid="recaptcha-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(MODE_LABELS).map(([k, l]) => <SelectItem key={k} value={k} data-testid={`recaptcha-mode-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {showV2 && (
          <div className="space-y-2 rounded-md border border-border p-3" data-testid="recaptcha-v2-fields">
            <div className="text-sm font-semibold">reCAPTCHA v2 — Checkbox</div>
            <Input placeholder="Site Key v2" value={form.v2_site_key} onChange={(e) => set("v2_site_key")(e.target.value)} data-testid="recaptcha-v2-site-key" />
            <Input type="password" placeholder={cfg.v2_secret_masked ? `Secret Key guardada: ${cfg.v2_secret_masked}` : "Secret Key v2"} value={form.v2_secret_key} onChange={(e) => set("v2_secret_key")(e.target.value)} data-testid="recaptcha-v2-secret-key" />
            <p className="text-[11px] text-muted-foreground">Deja la Secret Key vacía para conservar la guardada.</p>
          </div>
        )}
        {showV3 && (
          <div className="space-y-2 rounded-md border border-border p-3" data-testid="recaptcha-v3-fields">
            <div className="text-sm font-semibold">reCAPTCHA v3 — Invisible</div>
            <Input placeholder="Site Key v3" value={form.v3_site_key} onChange={(e) => set("v3_site_key")(e.target.value)} data-testid="recaptcha-v3-site-key" />
            <Input type="password" placeholder={cfg.v3_secret_masked ? `Secret Key guardada: ${cfg.v3_secret_masked}` : "Secret Key v3"} value={form.v3_secret_key} onChange={(e) => set("v3_secret_key")(e.target.value)} data-testid="recaptcha-v3-secret-key" />
            <label className="flex items-center gap-2 text-xs text-muted-foreground">Puntuación mínima (0–1)
              <Input type="number" step="0.1" min="0" max="1" className="h-8 w-24" value={form.v3_min_score} onChange={(e) => set("v3_min_score")(e.target.value)} data-testid="recaptcha-v3-min-score" />
            </label>
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="recaptcha-save-button"><Save size={14} className="mr-2" /> {saving ? "Guardando…" : "Guardar"}</Button>
      </div>
      <RecaptchaVerifier cfg={cfg} onVerified={setCfg} />
    </Card>
  );
}

function RecaptchaVerifier({ cfg, onVerified }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const versions = cfg.mode === "both" ? ["v2", "v3"] : [cfg.mode];
  const ready = versions.every((v) => cfg[`${v}_site_key`] && cfg[`${v}_secret_masked`]);
  if (!ready) return <p className="text-xs text-muted-foreground border-t border-border pt-3" data-testid="recaptcha-verify-hint">Guarda Site Key y Secret Key para poder verificar las claves con Google.</p>;
  const config = { enabled: true, mode: cfg.mode, v2_site_key: versions.includes("v2") ? cfg.v2_site_key : null, v3_site_key: versions.includes("v3") ? cfg.v3_site_key : null };
  const verify = async () => {
    setBusy(true);
    try {
      const tokens = await ref.current.getTokens();
      if (config.v2_site_key && !tokens.v2) { toast.error("Marca la casilla «No soy un robot» antes de verificar"); return; }
      let last = cfg;
      for (const v of versions) {
        const { data } = await api.post("/matriz/recaptcha/test", { version: v, token: tokens[v] });
        last = data;
        toast.success(`reCAPTCHA ${v} verificado${data.score != null ? ` · puntuación ${data.score}` : ""}`);
      }
      onVerified(last);
      ref.current.reset();
    } catch (e) { toast.error(apiError(e)); ref.current?.reset(); } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-border pt-4 space-y-3" data-testid="recaptcha-verifier">
      <div className="text-sm font-semibold">Verificación de claves</div>
      <p className="text-xs text-muted-foreground">Resuelve el reto con la Site Key configurada y pulsa verificar: Google confirmará que Site Key y Secret Key son el par correcto. Hasta entonces el widget no se muestra en el login.</p>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {versions.map((v) => (cfg[`${v}_verified`] ? <Badge key={v} className="bg-emerald-500 text-white" data-testid={`recaptcha-${v}-verified-badge`}>{v} verificado</Badge> : <Badge key={v} variant="outline" data-testid={`recaptcha-${v}-verified-badge`}>{v} sin verificar</Badge>))}
      </div>
      <RecaptchaBlock key={`${config.v2_site_key}|${config.v3_site_key}`} ref={ref} config={config} action="matriz_test" hideLabel />
      <Button variant="outline" onClick={verify} disabled={busy} data-testid="recaptcha-verify-button"><ShieldCheck size={14} className="mr-2" /> {busy ? "Verificando con Google…" : "Verificar claves con Google"}</Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SUBSCRIPTIONS
// ---------------------------------------------------------------------------
function SubscriptionsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/matriz/subscriptions").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Cargando…</div>;
  return (
    <div className="space-y-4" data-testid="subscriptions-tab">
      {!data.integrated && (
        <Card className="p-4 border-dashed">
          <div className="flex gap-3 items-start">
            <CreditCard size={20} className="text-muted-foreground" />
            <div>
              <div className="font-heading font-bold">Stripe SaaS no integrado</div>
              <p className="text-sm text-muted-foreground mt-1">{data.note}</p>
            </div>
          </div>
        </Card>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4"><div className="text-xs text-muted-foreground">Total</div><div className="font-heading text-2xl font-bold">{data.total}</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground">Activas</div><div className="font-heading text-2xl font-bold">{data.active}</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground">MRR</div><div className="font-heading text-2xl font-bold">{data.mrr_eur.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}</div></Card>
        <Card className="p-4"><div className="text-xs text-muted-foreground">Planes distintos</div><div className="font-heading text-2xl font-bold">{data.by_plan.length}</div></Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AUDIT
// ---------------------------------------------------------------------------
function AuditTab() {
  const [entries, setEntries] = useState([]);
  const [filters, setFilters] = useState({ action: "", actor: "", status: "", since_hours: 168 });
  const load = async () => {
    try {
      const qs = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
      qs.set("limit", "500");
      const r = await api.get(`/matriz/audit?${qs.toString()}`);
      setEntries(r.data.entries || []);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Input placeholder="Acción (ej: flag.)" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} data-testid="audit-filter-action" />
        <Input placeholder="Actor (email/id)" value={filters.actor} onChange={(e) => setFilters({ ...filters, actor: e.target.value })} data-testid="audit-filter-actor" />
        <Select value={filters.status || "any"} onValueChange={(v) => setFilters({ ...filters, status: v === "any" ? "" : v })}>
          <SelectTrigger data-testid="audit-filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Cualquier estado</SelectItem>
            <SelectItem value="success">success</SelectItem>
            <SelectItem value="denied">denied</SelectItem>
            <SelectItem value="error">error</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={load} data-testid="audit-search-btn">Filtrar</Button>
      </div>
      <div className="rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/40"><tr>
            <th className="text-left px-3 py-2">Fecha</th>
            <th className="text-left px-3 py-2">Acción</th>
            <th className="text-left px-3 py-2">Target</th>
            <th className="text-left px-3 py-2">Estado</th>
            <th className="text-left px-3 py-2">Actor</th>
            <th className="text-left px-3 py-2">Detalles</th>
          </tr></thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan={6} className="p-4 text-muted-foreground italic">Sin registros</td></tr>}
            {entries.map((e, i) => (
              <tr key={i} className="border-t border-border" data-testid={`audit-row-${i}`}>
                <td className="px-3 py-2 whitespace-nowrap">{new Date(e.at).toLocaleString("es-ES")}</td>
                <td className="px-3 py-2 font-mono">{e.action}</td>
                <td className="px-3 py-2 font-mono truncate max-w-[220px]">{e.target}</td>
                <td className="px-3 py-2">{e.status === "success" ? <Badge className="bg-emerald-500 text-white">success</Badge> : e.status === "denied" ? <Badge className="bg-red-500 text-white">denied</Badge> : <Badge variant="secondary">{e.status}</Badge>}</td>
                <td className="px-3 py-2">{e.actor_email || "—"}</td>
                <td className="px-3 py-2 max-w-[300px] truncate font-mono text-[10px] text-muted-foreground" title={JSON.stringify(e.details)}>{JSON.stringify(e.details)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-muted-foreground">{entries.length} registros</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CATEGORIES GLOBAL
// ---------------------------------------------------------------------------
function CategoriesGlobalTab() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ name: "", kind: "gasto", icon: "", color: "", enabled: true });

  const load = async () => {
    try { const r = await api.get("/matriz/categories/global"); setCats(r.data.categories || []); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Nombre requerido");
    try {
      await api.post("/matriz/categories/global", form);
      toast.success("Categoría creada");
      setForm({ name: "", kind: "gasto", icon: "", color: "", enabled: true });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggle = async (c) => {
    try { await api.put(`/matriz/categories/global/${c.id}`, { ...c, enabled: !c.enabled }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar categoría global?")) return;
    try { await api.delete(`/matriz/categories/global/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="categories-global-tab">
      <div className="rounded-xl border border-border p-3 bg-secondary/20">
        <div className="text-sm font-semibold mb-2">Añadir categoría global</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <Input placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" />
          <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
            <SelectTrigger data-testid="cat-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="gasto">Gasto</SelectItem>
              <SelectItem value="ingreso">Ingreso</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Icon (opc)" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} />
          <Input placeholder="#color (opc)" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
          <Button onClick={create} data-testid="cat-create"><PlusCircle size={14} className="mr-1" /> Añadir</Button>
        </div>
      </div>
      <div className="rounded-xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40"><tr>
            <th className="text-left px-3 py-2">Nombre</th>
            <th className="text-left px-3 py-2">Tipo</th>
            <th className="text-left px-3 py-2">Activa</th>
            <th className="text-left px-3 py-2">Icon/Color</th>
            <th className="text-right px-3 py-2"></th>
          </tr></thead>
          <tbody>
            {cats.length === 0 && <tr><td colSpan={5} className="p-4 text-xs italic text-muted-foreground">Sin categorías globales</td></tr>}
            {cats.map((c) => (
              <tr key={c.id} className="border-t border-border" data-testid={`cat-row-${c.id}`}>
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2 text-xs">{c.kind}</td>
                <td className="px-3 py-2"><Switch checked={c.enabled} onCheckedChange={() => toggle(c)} /></td>
                <td className="px-3 py-2 text-xs">{c.icon || "—"} · <span style={{ color: c.color }}>{c.color || "—"}</span></td>
                <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => remove(c.id)}><Trash2 size={14} /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// USERS (Gestión completa Fase 1 + Fase 2)
// ---------------------------------------------------------------------------
function UsersTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [filters, setFilters] = useState({ q: "", plan: "", country: "", status: "active" });
  const [plans, setPlans] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // user_id
  const [showCreate, setShowCreate] = useState(false);
  const [showQuick, setShowQuick] = useState(false);

  const load = async (goto = page) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
      qs.set("page", String(goto));
      qs.set("page_size", String(pageSize));
      const r = await api.get(`/matriz/users?${qs.toString()}`);
      setRows(r.data.users || []);
      setTotal(r.data.total || 0);
      setPage(r.data.page || goto);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.all([api.get("/matriz/plans"), api.get("/matriz/countries")]);
        setPlans(p.data.plans || []);
        setCountries(c.data.catalog || []);
      } catch { /* silent */ }
    })();
    load(1);
    // eslint-disable-next-line
  }, []);

  const applyFilters = () => load(1);

  const exportCsv = async () => {
    try {
      const qs = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
      const res = await api.get(`/matriz/users/export.csv?${qs.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "usuarios_matriz.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) { toast.error(apiError(e)); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4" data-testid="users-tab">
      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-8" placeholder="Buscar por email, nombre o id"
                 value={filters.q}
                 onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                 onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                 data-testid="users-search" />
        </div>
        <Select value={filters.status || "active"} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-36" data-testid="users-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="blocked">Bloqueados</SelectItem>
            <SelectItem value="deleted">Eliminados (soft)</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.plan || "any_plan"} onValueChange={(v) => setFilters({ ...filters, plan: v === "any_plan" ? "" : v })}>
          <SelectTrigger className="w-32" data-testid="users-filter-plan"><SelectValue placeholder="Plan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any_plan">Cualquier plan</SelectItem>
            {plans.map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.country || "any_country"} onValueChange={(v) => setFilters({ ...filters, country: v === "any_country" ? "" : v })}>
          <SelectTrigger className="w-32" data-testid="users-filter-country"><SelectValue placeholder="País" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any_country">Cualquier país</SelectItem>
            {countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={applyFilters} data-testid="users-apply-filters"><Search size={14} className="mr-1" /> Filtrar</Button>
        <Button variant="outline" onClick={exportCsv} data-testid="users-export-csv"><Download size={14} className="mr-1" /> CSV</Button>
        <PurgeTestButton onDone={() => load(1)} />
        <Button variant="outline" onClick={() => setShowQuick(true)} data-testid="users-quick-create"><UserPlus size={14} className="mr-1" /> Alta rápida</Button>
        <Button onClick={() => setShowCreate(true)} data-testid="users-create"><PlusCircle size={14} className="mr-1" /> Nuevo usuario</Button>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-3 py-2">Usuario</th>
              <th className="text-left px-3 py-2">Familia</th>
              <th className="text-left px-3 py-2">Plan / País</th>
              <th className="text-left px-3 py-2">Rol</th>
              <th className="text-left px-3 py-2">Estado</th>
              <th className="text-left px-3 py-2">Última actividad</th>
              <th className="text-left px-3 py-2">Creado</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground text-xs">Cargando…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground text-xs italic">Sin usuarios que coincidan</td></tr>}
            {!loading && rows.map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-muted/20" data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{u.name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </td>
                <td className="px-3 py-2 text-xs">{u.family_name || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  <div>{u.plan || "—"}</div>
                  <div className="text-muted-foreground">{u.country || "—"}</div>
                </td>
                <td className="px-3 py-2 text-xs">{u.family_role || "—"}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={u.status} />
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{u.last_activity ? new Date(u.last_activity).toLocaleString("es-ES") : "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString("es-ES") : "—"}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setSelected(u.id)} data-testid={`user-open-${u.id}`}>Abrir</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div>Total: <b>{total}</b> · Página {page} / {totalPages}</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => load(Math.max(1, page - 1))} disabled={page <= 1} data-testid="users-prev">Anterior</Button>
          <Button size="sm" variant="outline" onClick={() => load(Math.min(totalPages, page + 1))} disabled={page >= totalPages} data-testid="users-next">Siguiente</Button>
        </div>
      </div>

      {/* Drawer */}
      {selected && <UserDrawer userId={selected} onClose={() => setSelected(null)} onChanged={() => load(page)} plans={plans} countries={countries} />}
      {showCreate && <UserCreateDialog onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(1); }} plans={plans} countries={countries} />}
      {showQuick && <UserQuickDialog onClose={() => setShowQuick(false)} onCreated={() => { setShowQuick(false); load(1); }} />}
    </div>
  );
}

function PurgeTestButton({ onDone }) {
  const [preview, setPreview] = useState(null); // {count, matches}
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const runDry = async () => {
    setBusy(true);
    try {
      const r = await api.post("/matriz/users/purge-test?dry_run=true");
      setPreview(r.data);
      setOpen(true);
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const confirmPurge = async () => {
    setBusy(true);
    try {
      const r = await api.post("/matriz/users/purge-test?dry_run=false");
      toast.success(`Eliminados ${r.data.count} usuarios de prueba (${r.data.orphan_families_removed} familias huérfanas removidas)`);
      setOpen(false);
      setPreview(null);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <>
      <Button variant="outline" onClick={runDry} disabled={busy} data-testid="users-purge-test">
        <AlertTriangle size={14} className="mr-1" /> Limpiar usuarios de prueba
      </Button>
      {open && preview && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg bg-background border border-border rounded-xl p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="purge-dialog">
            <div className="flex items-center justify-between">
              <div className="font-heading font-bold text-lg flex items-center gap-2">
                <AlertTriangle className="text-amber-500" size={18} /> Limpiar usuarios de prueba
              </div>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}><X size={16} /></Button>
            </div>
            <div className="text-sm">Se eliminarán <b>{preview.count}</b> usuarios que coinciden con el patrón de test (<i>@radar.test</i>, <i>@test.es</i>, prefijos <i>test_</i>, <i>blocked_test_</i>, <i>nuevo_matriz_test</i>).</div>
            <div className="text-xs text-muted-foreground">Nunca se toca <b>admin@radar.es</b> ni ningún email fuera de esos patrones. También se limpiarán las familias que se queden sin miembros.</div>
            {preview.count > 0 && (
              <div className="rounded border border-border max-h-56 overflow-y-auto text-xs" data-testid="purge-list">
                {preview.matches.slice(0, 50).map((u) => (
                  <div key={u.id} className="px-2 py-1 border-b border-border last:border-b-0 font-mono flex justify-between">
                    <span>{u.email}</span><span className="text-muted-foreground">{u.name || "—"}</span>
                  </div>
                ))}
                {preview.count > 50 && <div className="px-2 py-1 text-center text-muted-foreground italic">… y {preview.count - 50} más</div>}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button variant="destructive" size="sm" onClick={confirmPurge} disabled={busy || preview.count === 0} data-testid="purge-confirm">
                <Trash2 size={14} className="mr-1" /> Eliminar {preview.count}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StatusBadge({ status }) {
  if (status === "blocked") return <Badge className="bg-red-500 text-white" data-testid="status-badge-blocked">Bloqueado</Badge>;
  if (status === "deleted") return <Badge variant="secondary" data-testid="status-badge-deleted">Eliminado</Badge>;
  return <Badge className="bg-emerald-500 text-white" data-testid="status-badge-active">Activo</Badge>;
}

function UserDrawer({ userId, onClose, onChanged, plans, countries }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("profile");
  const [form, setForm] = useState({ name: "", email: "", family_role: "admin", family_id: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/matriz/users/${userId}`);
      setData(r.data);
      const u = r.data.user;
      setForm({ name: u.name || "", email: u.email || "", family_role: u.family_role || "admin", family_id: u.family_id || "" });
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [userId]); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      const patch = {};
      if (form.name !== data.user.name) patch.name = form.name;
      if (form.email !== data.user.email) patch.email = form.email;
      if (form.family_role !== data.user.family_role) patch.family_role = form.family_role;
      if (form.family_id && form.family_id !== data.user.family_id) patch.family_id = form.family_id;
      if (Object.keys(patch).length === 0) { toast.info("Nada que guardar"); setBusy(false); return; }
      await api.put(`/matriz/users/${userId}`, patch);
      toast.success("Usuario actualizado");
      await load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const doStatus = async (action, reasonPrompt) => {
    let reason = "";
    if (reasonPrompt) {
      reason = window.prompt(reasonPrompt) || "";
      if (reason === null) return;
    }
    if (!window.confirm(`¿Confirmas la acción "${action}" sobre ${data?.user?.email}?`)) return;
    setBusy(true);
    try {
      await api.put(`/matriz/users/${userId}/status`, { action, reason });
      toast.success("Estado actualizado");
      await load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const hardDelete = async () => {
    const force = data?.user?.status !== "deleted";
    const msg = force
      ? "El usuario NO está en soft-delete. ¿Forzar hard-delete inmediato? Esta acción es IRREVERSIBLE."
      : "¿Ejecutar hard-delete definitivo? Esta acción es IRREVERSIBLE.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.delete(`/matriz/users/${userId}${force ? "?force=true" : ""}`);
      toast.success("Usuario eliminado definitivamente");
      onChanged?.();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  if (!data) {
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
        <div className="w-full max-w-lg bg-background border-l border-border p-6" onClick={(e) => e.stopPropagation()}>
          <div className="text-sm text-muted-foreground">Cargando ficha…</div>
        </div>
      </div>
    );
  }

  const u = data.user;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="user-drawer">
      <div className="w-full max-w-2xl bg-background border-l border-border overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-background z-10 border-b border-border p-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground">Ficha de usuario</div>
            <div className="font-heading text-lg font-bold">{u.name || u.email}</div>
            <div className="flex items-center gap-2 mt-1">
              <StatusBadge status={u.status} />
              <span className="text-xs text-muted-foreground">{u.email}</span>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="user-drawer-close"><X size={16} /></Button>
        </div>

        <div className="flex gap-1 border-b border-border px-4">
          {[
            { id: "profile", label: "Perfil" },
            { id: "sessions", label: `Sesiones (${data.sessions?.length || 0})` },
            { id: "history", label: `Historial (${data.audit_history?.length || 0})` },
            { id: "actor", label: `Acciones propias (${data.audit_as_actor?.length || 0})` },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
                    data-testid={`user-drawer-tab-${t.id}`}
                    className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === t.id ? "border-primary text-foreground" : "border-transparent text-muted-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-4 space-y-6">
          {tab === "profile" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <InfoRow label="ID">{u.id}</InfoRow>
                <InfoRow label="Rol familiar">{u.family_role}</InfoRow>
                <InfoRow label="Familia">{u.family_name || "—"}</InfoRow>
                <InfoRow label="Miembros de la familia">{data.family_members}</InfoRow>
                <InfoRow label="Plan">{u.plan || "—"}</InfoRow>
                <InfoRow label="País">{u.country || "—"}</InfoRow>
                <InfoRow label="Email verificado">{u.email_verified ? "Sí" : "No"}</InfoRow>
                <InfoRow label="2FA activo">{u.totp_enabled ? "Sí" : "No"}</InfoRow>
                <InfoRow label="Creado">{u.created_at ? new Date(u.created_at).toLocaleString("es-ES") : "—"}</InfoRow>
                <InfoRow label="Última actividad">{data.last_activity ? new Date(data.last_activity).toLocaleString("es-ES") : "—"}</InfoRow>
                {u.blocked_at && <InfoRow label="Bloqueado">{new Date(u.blocked_at).toLocaleString("es-ES")}</InfoRow>}
                {u.blocked_reason && <InfoRow label="Motivo bloqueo">{u.blocked_reason}</InfoRow>}
                {u.deleted_at && <InfoRow label="Soft-deleted">{new Date(u.deleted_at).toLocaleString("es-ES")}</InfoRow>}
              </div>

              <div className="rounded-xl border border-border p-3 space-y-2">
                <div className="text-sm font-semibold">Editar</div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs">
                    <span className="text-muted-foreground">Nombre</span>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-edit-name" />
                  </label>
                  <label className="block text-xs">
                    <span className="text-muted-foreground">Email</span>
                    <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-edit-email" />
                  </label>
                  <label className="block text-xs">
                    <span className="text-muted-foreground">Rol</span>
                    <Select value={form.family_role} onValueChange={(v) => setForm({ ...form, family_role: v })}>
                      <SelectTrigger data-testid="user-edit-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">admin</SelectItem>
                        <SelectItem value="editor">editor</SelectItem>
                        <SelectItem value="consulta">consulta</SelectItem>
                      </SelectContent>
                    </Select>
                  </label>
                  <label className="block text-xs">
                    <span className="text-muted-foreground">Family ID (mover a otra familia)</span>
                    <Input value={form.family_id} onChange={(e) => setForm({ ...form, family_id: e.target.value })} data-testid="user-edit-family" />
                  </label>
                </div>
                <div className="flex justify-end">
                  <Button size="sm" onClick={save} disabled={busy} data-testid="user-save">
                    <Save size={14} className="mr-1" /> Guardar
                  </Button>
                </div>
              </div>

              <div className="rounded-xl border border-border p-3 space-y-2">
                <div className="text-sm font-semibold flex items-center gap-1"><ShieldCheck size={14} /> Acciones administrativas</div>
                <div className="flex flex-wrap gap-2">
                  {u.status === "active" && (
                    <Button size="sm" variant="outline" onClick={() => doStatus("block", "Motivo del bloqueo (opcional):")} disabled={busy} data-testid="user-action-block">
                      <Ban size={14} className="mr-1" /> Bloquear
                    </Button>
                  )}
                  {u.status === "blocked" && (
                    <Button size="sm" variant="outline" onClick={() => doStatus("unblock", null)} disabled={busy} data-testid="user-action-unblock">
                      <RotateCcw size={14} className="mr-1" /> Desbloquear
                    </Button>
                  )}
                  {u.status !== "deleted" && (
                    <Button size="sm" variant="outline" onClick={() => doStatus("soft_delete", "Motivo del borrado (opcional):")} disabled={busy} data-testid="user-action-soft-delete">
                      <Trash2 size={14} className="mr-1" /> Soft-delete
                    </Button>
                  )}
                  {u.status === "deleted" && (
                    <Button size="sm" variant="outline" onClick={() => doStatus("restore", null)} disabled={busy} data-testid="user-action-restore">
                      <RotateCcw size={14} className="mr-1" /> Restaurar
                    </Button>
                  )}
                  <Button size="sm" variant="destructive" onClick={hardDelete} disabled={busy} data-testid="user-action-hard-delete">
                    <Trash2 size={14} className="mr-1" /> Hard-delete
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground">
                  Hard-delete solo permitido si el usuario lleva &gt;30 días en soft-delete. Fuerza (<i>force=true</i>) queda auditada.
                </div>
              </div>
            </>
          )}

          {tab === "sessions" && (
            <div className="rounded-xl border border-border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/40"><tr>
                  <th className="text-left px-3 py-2">Sesión</th>
                  <th className="text-left px-3 py-2">Última vez</th>
                  <th className="text-left px-3 py-2">IP</th>
                  <th className="text-left px-3 py-2">Agent</th>
                </tr></thead>
                <tbody>
                  {(data.sessions || []).length === 0 && <tr><td colSpan={4} className="p-3 text-center text-muted-foreground italic">Sin sesiones activas</td></tr>}
                  {(data.sessions || []).map((s) => (
                    <tr key={s.id} className="border-t border-border">
                      <td className="px-3 py-2 font-mono">{(s.id || "").slice(0, 8)}…</td>
                      <td className="px-3 py-2">{s.last_seen ? new Date(s.last_seen).toLocaleString("es-ES") : "—"}</td>
                      <td className="px-3 py-2 font-mono">{s.ip || "—"}</td>
                      <td className="px-3 py-2 truncate max-w-[200px]">{s.user_agent || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "history" && <AuditList entries={data.audit_history} testid="user-history" />}
          {tab === "actor" && <AuditList entries={data.audit_as_actor} testid="user-actor-history" />}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm font-mono break-all">{children ?? "—"}</div>
    </div>
  );
}

function AuditList({ entries, testid }) {
  return (
    <div className="rounded-xl border border-border overflow-hidden" data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-muted/40"><tr>
          <th className="text-left px-3 py-2">Fecha</th>
          <th className="text-left px-3 py-2">Acción</th>
          <th className="text-left px-3 py-2">Estado</th>
          <th className="text-left px-3 py-2">Actor</th>
          <th className="text-left px-3 py-2">Target</th>
          <th className="text-left px-3 py-2">Detalles</th>
        </tr></thead>
        <tbody>
          {(!entries || entries.length === 0) && <tr><td colSpan={6} className="p-3 text-center text-muted-foreground italic">Sin registros</td></tr>}
          {(entries || []).map((e, i) => (
            <tr key={i} className="border-t border-border">
              <td className="px-3 py-2 whitespace-nowrap">{new Date(e.at).toLocaleString("es-ES")}</td>
              <td className="px-3 py-2 font-mono">{e.action}</td>
              <td className="px-3 py-2">{e.status}</td>
              <td className="px-3 py-2">{e.actor_email || "—"}</td>
              <td className="px-3 py-2 font-mono truncate max-w-[180px]">{e.target}</td>
              <td className="px-3 py-2 font-mono max-w-[220px] truncate" title={JSON.stringify(e.details)}>{JSON.stringify(e.details)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CredentialsBox({ creds }) {
  const [copied, setCopied] = useState(false);
  if (!creds) return null;
  const copy = (v) => { navigator.clipboard.writeText(v); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 space-y-2 text-sm" data-testid="creds-box">
      <div className="flex items-center gap-2 font-semibold"><KeyRound size={14} /> Credenciales generadas</div>
      {creds.sent ? (
        <div className="text-xs">Enviadas por email a <b>{creds.email}</b>.</div>
      ) : (
        <div className="text-xs text-muted-foreground">{creds.message} {creds.error && <span className="text-red-500 block">Error: {creds.error}</span>}</div>
      )}
      {creds.password && (
        <div className="grid grid-cols-1 gap-1 font-mono text-xs">
          <div className="flex items-center gap-2"><span className="text-muted-foreground">Email:</span> {creds.email} <Button size="sm" variant="ghost" onClick={() => copy(creds.email)}><Copy size={12} /></Button></div>
          <div className="flex items-center gap-2"><span className="text-muted-foreground">Password:</span> {creds.password} <Button size="sm" variant="ghost" onClick={() => copy(creds.password)}><Copy size={12} /></Button></div>
          <div className="flex items-center gap-2"><span className="text-muted-foreground">Login:</span> <a href={creds.login_link} className="underline">{creds.login_link}</a></div>
          {copied && <div className="text-emerald-600 text-[10px]">Copiado</div>}
        </div>
      )}
    </div>
  );
}

function UserQuickDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null);

  const submit = async () => {
    if (!form.email.trim()) return toast.error("Email requerido");
    setBusy(true);
    try {
      const r = await api.post("/matriz/users/quick", form);
      setCreds(r.data.credentials);
      toast.success("Usuario creado");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-background border border-border rounded-xl p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="user-quick-dialog">
        <div className="flex items-center justify-between">
          <div className="font-heading font-bold text-lg">Alta rápida</div>
          <Button variant="ghost" size="sm" onClick={onClose}><X size={16} /></Button>
        </div>
        <div className="text-xs text-muted-foreground">Solo email + nombre. Se genera password temporal y una familia individual nueva.</div>
        <div className="space-y-2">
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quick-email" />
          <Input placeholder="Nombre (opcional)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quick-name" />
        </div>
        <CredentialsBox creds={creds} />
        <div className="flex justify-end gap-2">
          {creds ? (
            <Button onClick={() => { onCreated?.(); }} data-testid="quick-done">Finalizar</Button>
          ) : (
            <Button onClick={submit} disabled={busy} data-testid="quick-submit"><UserPlus size={14} className="mr-1" /> Crear</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function UserCreateDialog({ onClose, onCreated, plans, countries }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", family_id: "", family_role: "admin", plan: "individual", country: "", send_credentials: false });
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null);
  const [tempPwd, setTempPwd] = useState(null);

  const submit = async () => {
    if (!form.email.trim() || !form.name.trim()) return toast.error("Email y nombre requeridos");
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.family_id) delete payload.family_id;
      if (!payload.password) delete payload.password;
      const r = await api.post("/matriz/users", payload);
      setTempPwd(r.data.temp_password);
      setCreds(r.data.credentials || null);
      toast.success("Usuario creado");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-xl bg-background border border-border rounded-xl p-4 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="user-create-dialog">
        <div className="flex items-center justify-between">
          <div className="font-heading font-bold text-lg">Nuevo usuario</div>
          <Button variant="ghost" size="sm" onClick={onClose}><X size={16} /></Button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs col-span-2"><span className="text-muted-foreground">Email *</span>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-email" />
          </label>
          <label className="text-xs col-span-2"><span className="text-muted-foreground">Nombre *</span>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-name" />
          </label>
          <label className="text-xs col-span-2"><span className="text-muted-foreground">Password (vacío = generar temporal)</span>
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="create-password" />
          </label>
          <label className="text-xs"><span className="text-muted-foreground">Rol</span>
            <Select value={form.family_role} onValueChange={(v) => setForm({ ...form, family_role: v })}>
              <SelectTrigger data-testid="create-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">admin</SelectItem>
                <SelectItem value="editor">editor</SelectItem>
                <SelectItem value="consulta">consulta</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <label className="text-xs"><span className="text-muted-foreground">Plan (nueva familia)</span>
            <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
              <SelectTrigger data-testid="create-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                {plans.filter((p) => p.enabled).map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="text-xs"><span className="text-muted-foreground">País (nueva familia)</span>
            <Select value={form.country || "none"} onValueChange={(v) => setForm({ ...form, country: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="create-country"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <label className="text-xs col-span-2"><span className="text-muted-foreground">Family ID existente (opcional — deja vacío para crear una nueva familia)</span>
            <Input value={form.family_id} onChange={(e) => setForm({ ...form, family_id: e.target.value })} data-testid="create-family-id" />
          </label>
          <label className="flex items-center gap-2 col-span-2 text-xs">
            <Switch checked={form.send_credentials} onCheckedChange={(v) => setForm({ ...form, send_credentials: v })} data-testid="create-send-creds" />
            <span>Enviar credenciales por email (Resend). Si no hay API key configurada se mostrarán en pantalla.</span>
          </label>
        </div>
        {tempPwd && !creds && (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm">
            <div className="font-semibold">Password temporal generada</div>
            <div className="font-mono text-xs mt-1">{tempPwd}</div>
            <div className="text-[10px] text-muted-foreground mt-1">Cópiala ya, no se volverá a mostrar.</div>
          </div>
        )}
        <CredentialsBox creds={creds} />
        <div className="flex justify-end gap-2">
          {tempPwd ? (
            <Button onClick={() => { onCreated?.(); }} data-testid="create-done">Finalizar</Button>
          ) : (
            <Button onClick={submit} disabled={busy} data-testid="create-submit"><UserPlus size={14} className="mr-1" /> Crear usuario</Button>
          )}
        </div>
      </div>
    </div>
  );
}
